import React, { useEffect, useState, memo } from 'react'
import { useIntl } from 'react-intl'
import { chunk } from 'lodash'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Paper, IconButton } from '@material-ui/core'
import { ExpandMore, ExpandLess } from '@material-ui/icons'
import messages from '@utils/messages'

import { StructureSearchInitialQueryVars } from './StructureSearchDraw'

const useStyles = makeStyles((theme: Theme) => ({
  recentSearchesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '16%',
    marginTop: theme.spacing(30),
    height: '95%',
  },
  sectionHeading: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(2),
  },
  carousel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    height: '100%',
  },
  recentSearchesImages: {
    height: theme.typography.pxToRem(685),
  },
  imageContainer: {
    height: '10rem',
    width: '10rem',
    margin: theme.spacing(2, 0),
    border: `1px solid ${theme.palette.grey['400']}`,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    '& img': {
      width: 'auto',
      height: 'auto',
      maxHeight: '100%',
      maxWidth: '85%',
    },
  },
  arrowButton: {
    padding: 0,
  },
  arrowIconEnabled: {
    transform: 'scale(1.5)',
    color: theme.palette.secondary.main,
  },
  arrowIconDisabled: {
    transform: 'scale(1.5)',
    color: theme.palette.grey[500],
  },
}))

const StructureImage = ({
  structure,
  setRecentSearchSelection,
  formatMessage,
  id,
}): JSX.Element => {
  const classes = useStyles()
  const [structureImage, setStructureImage] = useState('')
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const imageKey = structure.image
      const structureImage = sessionStorage.getItem(imageKey)
      structureImage && setStructureImage(structureImage)
    }
  }, [structure.image])
  return (
    <Paper
      square
      elevation={0}
      className={classes.imageContainer}
      onClick={() => setRecentSearchSelection(structure)}
      id={id}
    >
      <img
        src={structureImage}
        alt={formatMessage(messages.STRUCTURE_QUERY_IMAGE_ALT)}
      />
    </Paper>
  )
}

interface RecentStructureSearchesProps {
  setRecentSearchSelection: (
    recentSearch: StructureSearchInitialQueryVars
  ) => void
  recentStructures: StructureSearchInitialQueryVars[]
}
const RecentStructureSearches: React.FC<RecentStructureSearchesProps> = ({
  setRecentSearchSelection,
  recentStructures,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [structureGroups, setStructureGroups] = useState<
    StructureSearchInitialQueryVars[][]
  >([])
  const [groupToShow, setGroupToShow] = useState<number>(0)

  useEffect(() => {
    const orderStructuresNewestToOldest = recentStructures.reverse()
    const chunkedStructures = chunk(orderStructuresNewestToOldest, 4)
    setStructureGroups(chunkedStructures)
  }, [recentStructures])
  const firstStructureGroup = 0
  const lastStructureGroup = structureGroups.length - 1
  const upDisabled = groupToShow === firstStructureGroup
  const downDisabled = groupToShow === lastStructureGroup

  return structureGroups[groupToShow]?.length > 0 ? (
    <div className={classes.recentSearchesContainer}>
      <p className={classes.sectionHeading}>
        {formatMessage(messages.RECENT_SEARCHES)}
      </p>
      <div className={classes.carousel}>
        {structureGroups.length > 1 && (
          <IconButton
            disabled={upDisabled}
            onClick={() => setGroupToShow(groupToShow - 1)}
            className={classes.arrowButton}
          >
            <ExpandLess
              className={
                upDisabled
                  ? classes.arrowIconDisabled
                  : classes.arrowIconEnabled
              }
            />
          </IconButton>
        )}
        <div className={classes.recentSearchesImages}>
          {structureGroups[groupToShow].map(
            (structure, i) =>
              structure.image && (
                <StructureImage
                  key={`${structure.image}-${i}`}
                  id={`${structure.image}-${i}`}
                  setRecentSearchSelection={setRecentSearchSelection}
                  structure={structure}
                  formatMessage={formatMessage}
                />
              )
          )}
        </div>
        {structureGroups.length > 1 && (
          <IconButton
            onClick={() => setGroupToShow(groupToShow + 1)}
            className={classes.arrowButton}
            disabled={downDisabled}
          >
            <ExpandMore
              className={
                downDisabled
                  ? classes.arrowIconDisabled
                  : classes.arrowIconEnabled
              }
            />
          </IconButton>
        )}
      </div>
    </div>
  ) : null
}

export default memo(RecentStructureSearches)
